import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useProjectStore from '../stores/projectStore';
import { useSeoCheck } from '../hooks/useSeoCheck';

const SIGNAL_COLORS = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  fail: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-gray-50 text-gray-600 border-gray-200',
};
const SIGNAL_ICONS = { ok: '✓', warn: '!', fail: '✕', info: 'i' };

const SIGNAL_LABELS = {
  title: 'Title-Tag',
  meta_description: 'Meta-Description',
  indexable: 'Indexierbarkeit',
  canonical: 'Canonical',
  h1: 'H1-Struktur',
  viewport: 'Viewport / Mobile',
  html_lang: 'Sprach-Attribut',
  alt_texts: 'Bild-Alt-Texte',
  https: 'HTTPS',
  word_count: 'Content-Umfang',
  robots_txt: 'robots.txt',
  sitemap: 'Sitemap',
  og: 'Open Graph',
  hreflang: 'hreflang',
};

export default function SeoCheck() {
  const [domain, setDomain] = useState('');
  const navigate = useNavigate();
  const projects = useProjectStore((s) => s.projects);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const check = useSeoCheck();

  const submit = (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    check.mutate({ url: domain.trim() });
  };

  const jumpToProject = (id) => {
    if (!id) return;
    setCurrentProject(Number(id));
    navigate('/seo/rankings');
  };

  const hasResult = !!check.data && !check.isPending;
  const audit = check.data?.audit;
  const pagespeed = check.data?.pagespeed;
  const xovi = check.data?.xovi;

  return (
    <div className="px-4 pt-12 pb-16">
      <div className="w-full max-w-3xl mx-auto">
        <h1 className="text-[26px] font-semibold text-[#0D3B50] text-center mb-1">SEO-Check</h1>
        <p className="text-[13.5px] text-gray-500 text-center mb-7">
          Domain eingeben — Onpage-Faktoren und PageSpeed abfragen.
        </p>

        <form onSubmit={submit} className="relative">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            type="text"
            placeholder="domain.de"
            autoFocus
            disabled={check.isPending}
            className="w-full bg-white border border-gray-200 rounded-full pl-6 pr-32 py-4 text-[15.5px] shadow-sm outline-none focus:border-[#0D3B50] focus:ring-2 focus:ring-[#0D3B50]/10 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!domain.trim() || check.isPending}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#0D3B50] text-white text-[13.5px] font-medium px-5 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50"
          >
            {check.isPending ? 'Prüfe…' : 'Prüfen'}
          </button>
        </form>

        {projects?.length > 0 && !hasResult && !check.isPending && (
          <div className="mt-8 flex items-center justify-center gap-2 text-[12.5px] text-gray-500">
            <span>oder in ein Projekt springen:</span>
            <select
              defaultValue=""
              onChange={(e) => jumpToProject(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[12.5px] outline-none focus:border-[#0D3B50]"
            >
              <option value="" disabled>Projekt wählen…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        {check.isError && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-[13px]">
            <strong>Fehler:</strong> {check.error?.response?.data?.message || check.error?.message}
          </div>
        )}

        {hasResult && (
          <div className="mt-8 space-y-4">
            <ResultHeader audit={audit} />
            <SignalsCard signals={audit.signals} />
            <PageDetailsCard page={audit.page} robots={audit.robots_txt} sitemap={audit.sitemap} />
            <PageSpeedCard data={pagespeed} />
            <XoviCard data={xovi} />
          </div>
        )}
      </div>
    </div>
  );
}

function ResultHeader({ audit }) {
  return (
    <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[11.5px] text-gray-400 uppercase tracking-wider">Geprüft</div>
        <div className="text-[14.5px] font-medium text-[#0D3B50] truncate">{audit.final_url || audit.url}</div>
      </div>
      <div className="flex items-center gap-3 text-[12px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          HTTP {audit.status}
        </span>
        {audit.redirects?.length > 0 && (
          <span>{audit.redirects.length} Redirect{audit.redirects.length === 1 ? '' : 's'}</span>
        )}
      </div>
    </div>
  );
}

function SignalsCard({ signals }) {
  const entries = Object.entries(signals || {});
  return (
    <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm">
      <h3 className="font-semibold text-[14.5px] text-[#0D3B50] mb-3">Onpage-Signale</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {entries.map(([k, v]) => (
          <div
            key={k}
            className={`flex items-start gap-2 border rounded-lg px-3 py-2 text-[12.5px] ${SIGNAL_COLORS[v.status] || SIGNAL_COLORS.info}`}
          >
            <span className="font-bold mt-0.5 leading-none">{SIGNAL_ICONS[v.status] || '·'}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{SIGNAL_LABELS[k] || k}</div>
              <div className="opacity-80">{v.msg}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PageDetailsCard({ page, robots, sitemap }) {
  return (
    <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm">
      <h3 className="font-semibold text-[14.5px] text-[#0D3B50] mb-3">Inhalte & Meta</h3>
      <dl className="text-[12.5px] divide-y divide-gray-100">
        <Row label="Title">{page.title || <em className="text-gray-400">—</em>}<span className="text-gray-400 ml-2">({page.title_length})</span></Row>
        <Row label="Meta-Description">
          {page.meta_description || <em className="text-gray-400">—</em>}
          <span className="text-gray-400 ml-2">({page.meta_description_length})</span>
        </Row>
        <Row label="Canonical">{page.canonical || <em className="text-gray-400">—</em>}</Row>
        <Row label="Robots-Meta">{page.meta_robots || <em className="text-gray-400">—</em>}</Row>
        <Row label="Erste H1">{page.h1_first || <em className="text-gray-400">—</em>}</Row>
        <Row label="Struktur">{page.h1_count} × H1 · {page.h2_count} × H2 · {page.word_count} Wörter</Row>
        <Row label="Bilder">{page.images_count} gesamt · {page.images_without_alt} ohne alt</Row>
        <Row label="Links">{page.internal_links} intern · {page.external_links} extern · {page.nofollow_links} nofollow</Row>
        <Row label="Open Graph">
          {page.og.title ? '✓ Title' : '—'} · {page.og.image ? '✓ Image' : '—'} · {page.og.type || '—'}
        </Row>
        <Row label="JSON-LD">
          {page.json_ld.count > 0
            ? `${page.json_ld.count} Block${page.json_ld.count === 1 ? '' : 's'} · ${page.json_ld.types.join(', ') || '—'}`
            : <em className="text-gray-400">nicht gefunden</em>}
        </Row>
        <Row label="hreflang">{page.hreflang?.length || 0} Einträge</Row>
        <Row label="robots.txt">
          {robots?.reachable
            ? <a href={robots.url} target="_blank" rel="noreferrer" className="text-teal underline">{robots.url}</a>
            : <em className="text-gray-400">nicht erreichbar</em>}
          {robots?.sitemaps?.length > 0 && (
            <span className="text-gray-400 ml-2">{robots.sitemaps.length} Sitemap-Einträge</span>
          )}
        </Row>
        <Row label="Sitemap">
          {sitemap?.reachable
            ? <a href={sitemap.url} target="_blank" rel="noreferrer" className="text-teal underline">{sitemap.url}</a>
            : <em className="text-gray-400">nicht erreichbar</em>}
        </Row>
      </dl>
    </div>
  );
}

function PageSpeedCard({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm">
      <h3 className="font-semibold text-[14.5px] text-[#0D3B50] mb-3">PageSpeed</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {['mobile', 'desktop'].map((s) => (
          <PsiStrategy key={s} strategy={s} data={data[s]} />
        ))}
      </div>
    </div>
  );
}

function PsiStrategy({ strategy, data }) {
  if (!data) return null;
  if (!data.ok) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 text-[12.5px]">
        <div className="font-medium text-[#0D3B50] capitalize mb-1">{strategy}</div>
        <div className="text-amber-700">{data.error || 'PageSpeed konnte nicht geladen werden.'}</div>
        {data.error?.includes('429') && (
          <div className="text-[11.5px] text-gray-500 mt-1">
            Tipp: <code>GOOGLE_PAGESPEED_KEY</code> in der Backend-.env setzen für stabile Aufrufe.
          </div>
        )}
      </div>
    );
  }
  const score = data.performance_score;
  const color = score >= 90 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-medium text-[#0D3B50] capitalize">{strategy}</div>
        <div className={`text-[26px] font-semibold ${color}`}>{score ?? '—'}</div>
      </div>
      <dl className="text-[12px] grid grid-cols-2 gap-x-3 gap-y-1">
        {Object.entries(data.metrics || {}).map(([k, v]) => {
          if (!v) return null;
          return (
            <div key={k} className="contents">
              <dt className="text-gray-500 uppercase">{k}</dt>
              <dd className="text-right text-gray-700">{v.display || '—'}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function XoviCard({ data }) {
  if (!data) return null;
  if (!data.ok) {
    return (
      <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm">
        <h3 className="font-semibold text-[14.5px] text-[#0D3B50] mb-2">Sichtbarkeit (XOVI)</h3>
        <div className="text-[12.5px] text-gray-500">
          {data.message || (data.reason === 'not_configured'
            ? 'XOVI-API-Key ist im Backend noch nicht hinterlegt.'
            : 'Keine XOVI-Daten verfügbar.')}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-black/[.07] p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-semibold text-[14.5px] text-[#0D3B50]">Sichtbarkeit (XOVI Monitor)</h3>
        <div className="text-[11.5px] text-gray-400">{data.host}</div>
      </div>

      {!data.in_monitor ? (
        <div className="text-[12.5px] text-gray-600">
          <p className="mb-2">Für diese Domain liegen <strong>keine Keywords im XOVI-Monitor</strong>. Volumen-Daten sind erst verfügbar, sobald Keywords getrackt werden.</p>
          {data.page_candidates?.length > 0 && (
            <div>
              <div className="text-[11.5px] text-gray-500 mb-1">Auffällige Begriffe aus Title / H1 / Meta-Description (als Tracking-Startpunkt):</div>
              <div className="flex flex-wrap gap-1.5">
                {data.page_candidates.map((kw) => (
                  <span key={kw} className="text-[11.5px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">{kw}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="text-[12px] text-gray-500 mb-2">
            {data.total_keywords} Keyword{data.total_keywords === 1 ? '' : 's'} im Monitor · Top {Math.min(data.top.length, data.total_keywords)} nach Volumen
          </div>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
                  <th className="text-left font-medium py-1.5 px-2">Keyword</th>
                  <th className="text-right font-medium py-1.5 px-2">Volumen</th>
                  <th className="text-right font-medium py-1.5 px-2">CPC</th>
                  <th className="text-right font-medium py-1.5 px-2">Position</th>
                </tr>
              </thead>
              <tbody>
                {data.top.map((k) => (
                  <tr key={k.keyword} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-1.5 px-2 text-gray-800">{k.keyword}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{k.sevo ?? '—'}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-gray-500">
                      {k.cpc != null ? k.cpc.toFixed(2) + ' €' : '—'}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">
                      {k.position == null
                        ? '—'
                        : k.position < 0
                          ? <span className="text-gray-400">&gt;100</span>
                          : Number(k.position).toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="text-gray-500 w-32 shrink-0">{label}</dt>
      <dd className="flex-1 min-w-0 text-gray-800 break-words">{children}</dd>
    </div>
  );
}
